import { useEffect, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useRecoilValue } from "recoil";

import {
  CLIP_QUALITY_CONFIGS,
  UPDATE_CLIP_QUALITY_CONFIG,
} from "MVVM/GraphQL/clipQualityConfigs";
import { AllstarUser } from "MVVM/State";

export interface ClipQualityConfigSettings {
  resolution: string;
  framerate: number;
  frameBlendBlur: number;
}

export interface ClipQualityOption {
  configName: string;
  settings: ClipQualityConfigSettings;
  requiredUserGroup: string;
  canApply: boolean;
}

export interface ClipQualityConfigsData {
  clipQualityConfigs: {
    configs: ClipQualityOption[];
    currentConfig: string | null;
    needsConfChange: boolean;
  };
}

const DEFAULT_TIER_CONFIGS: Record<string, string> = {
  FREE: "720p30",
  ESSENTIALS: "1080p60",
  PRO: "1080p120",
  PRO_PLUS: "1080p120",
  CREATOR_PLAT: "1440p120@360",
  INTERNAL: "1440p120@360",
};

const TIER_ORDER: Record<string, number> = {
  FREE: 0,
  ESSENTIALS: 1,
  PRO: 2,
  CREATOR_PLAT: 3,
};

const TIER_DISPLAY: Record<string, string> = {
  FREE: "FREE",
  ESSENTIALS: "BASIC",
  PRO: "STANDARD",
  CREATOR_PLAT: "UNLIMITED",
};

function parseResolutionHeight(resolution: string): number {
  const match = resolution.match(/(\d+)x(\d+)/);
  return match ? parseInt(match[2], 10) : 0;
}

export const DEFAULT_ASPECT_RATIO = "16:9";

const KNOWN_ASPECT_RATIOS: { label: string; ratio: number }[] = [
  { label: "16:9", ratio: 16 / 9 },
  { label: "4:3", ratio: 4 / 3 },
  { label: "5:4", ratio: 5 / 4 },
  { label: "16:10", ratio: 16 / 10 },
  { label: "9:16", ratio: 9 / 16 },
];

export function aspectRatioOfResolution(resolution: string): string {
  const match = resolution.match(/(\d+)x(\d+)/);
  if (!match) return DEFAULT_ASPECT_RATIO;
  const ratio = parseInt(match[1], 10) / parseInt(match[2], 10);
  const known = KNOWN_ASPECT_RATIOS.find(
    (ar) => Math.abs(ar.ratio - ratio) < 0.01,
  );
  return known ? known.label : `${match[1]}:${match[2]}`;
}

function buildAspectRatioOptions(configs: ClipQualityOption[]): string[] {
  const available = new Set(
    configs.map((c) => aspectRatioOfResolution(c.settings.resolution)),
  );
  const knownOrder = KNOWN_ASPECT_RATIOS.map((ar) => ar.label).filter((label) =>
    available.has(label),
  );
  const unknown = Array.from(available).filter(
    (label) => !knownOrder.includes(label),
  );
  return [...knownOrder, ...unknown];
}

function resolutionLabel(resolution: string): string {
  return `${parseResolutionHeight(resolution)}p`;
}

const BLUR_LABELS: Record<number, string> = {
  1: "Disabled",
  3: "Low",
  5: "Medium",
  10: "High",
};

function buildBlurLabelMap(values: number[]): Record<number, string> {
  if (values.length === 0) return {};
  const sorted = [...values].sort((a, b) => a - b);
  const map: Record<number, string> = {};
  for (const val of sorted) {
    map[val] = BLUR_LABELS[val] ?? `${val}x`;
  }
  return map;
}

export interface SliderStep {
  value: string | number;
  label: string;
  hoverText?: string;
  tierRequired: string;
  tierDisplay: string;
  showTierTag: boolean;
}

export interface SliderConfig {
  key: string;
  label: string;
  steps: SliderStep[];
}

function getMinTierForValue(
  configs: ClipQualityOption[],
  settingKey: keyof ClipQualityConfigSettings,
  value: string | number,
): string {
  let minTier = "CREATOR_PLAT";
  for (const conf of configs) {
    const confValue = conf.settings[settingKey];
    if (confValue === value) {
      if (TIER_ORDER[conf.requiredUserGroup] < TIER_ORDER[minTier]) {
        minTier = conf.requiredUserGroup;
      }
    }
  }
  return minTier;
}

function buildSliderSteps(
  configs: ClipQualityOption[],
  settingKey: keyof ClipQualityConfigSettings,
  labelMap?: Record<string | number, string>,
): SliderStep[] {
  const uniqueValues = new Set<string | number>();
  for (const conf of configs) {
    uniqueValues.add(conf.settings[settingKey]);
  }

  const sorted = Array.from(uniqueValues).sort((a, b) => {
    if (settingKey === "resolution") {
      return (
        parseResolutionHeight(a as string) - parseResolutionHeight(b as string)
      );
    }
    return (a as number) - (b as number);
  });

  const steps: SliderStep[] = sorted.map((val) => {
    const tier = getMinTierForValue(configs, settingKey, val);
    const display = TIER_DISPLAY[tier] || tier;
    let label: string;
    if (labelMap) {
      label = labelMap[val] ?? String(val);
    } else if (settingKey === "resolution") {
      label = resolutionLabel(val as string);
    } else {
      label = String(val);
    }

    return {
      value: val,
      label,
      hoverText: settingKey === "resolution" ? String(val) : undefined,
      tierRequired: tier,
      tierDisplay: display,
      showTierTag: false,
    };
  });

  const lowestTier = steps.reduce(
    (min, s) => Math.min(min, TIER_ORDER[s.tierRequired] ?? 0),
    Infinity,
  );

  for (const step of steps) {
    const tierLevel = TIER_ORDER[step.tierRequired] ?? 0;
    step.showTierTag = tierLevel > lowestTier;
  }

  return steps;
}

function findConfigForSelection(
  configs: ClipQualityOption[],
  resolution: string,
  framerate: number,
  frameBlendBlur: number,
): ClipQualityOption | null {
  return (
    configs.find(
      (c) =>
        c.settings.resolution === resolution &&
        c.settings.framerate === framerate &&
        c.settings.frameBlendBlur === frameBlendBlur,
    ) ?? null
  );
}

function isStepAvailable(
  configs: ClipQualityOption[],
  settingKey: keyof ClipQualityConfigSettings,
  value: string | number,
  currentResolution: string,
  currentFramerate: number,
  currentBlur: number,
): boolean {
  return configs.some((c) => {
    if (c.settings[settingKey] !== value) return false;
    if (
      settingKey !== "resolution" &&
      c.settings.resolution !== currentResolution
    )
      return false;
    if (settingKey !== "framerate" && c.settings.framerate !== currentFramerate)
      return false;
    if (
      settingKey !== "frameBlendBlur" &&
      c.settings.frameBlendBlur !== currentBlur
    )
      return false;
    return true;
  });
}

export const useClipQualityConfigs = (
  selectedAspectRatio: string = DEFAULT_ASPECT_RATIO,
) => {
  const allstarUser = useRecoilValue(AllstarUser);
  const isLoggedIn = allstarUser.loggedIn;
  const prevLoggedInRef = useRef(isLoggedIn);

  const { data, loading, error, refetch } =
    useQuery<ClipQualityConfigsData>(CLIP_QUALITY_CONFIGS);

  useEffect(() => {
    if (isLoggedIn && !prevLoggedInRef.current) {
      refetch();
    }
    prevLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, refetch]);

  const [updateConfig, { loading: saving }] = useMutation(
    UPDATE_CLIP_QUALITY_CONFIG,
  );

  const configs = data?.clipQualityConfigs?.configs ?? [];
  const currentConfig = data?.clipQualityConfigs?.currentConfig ?? null;
  const needsConfChange = data?.clipQualityConfigs?.needsConfChange ?? false;

  const aspectRatios = buildAspectRatioOptions(configs);
  const configsForAspect = configs.filter(
    (c) =>
      aspectRatioOfResolution(c.settings.resolution) === selectedAspectRatio,
  );

  const resolutionSlider = buildSliderSteps(configsForAspect, "resolution");
  const framerateSlider = buildSliderSteps(configsForAspect, "framerate");

  const blurValues = Array.from(
    new Set(configsForAspect.map((c) => c.settings.frameBlendBlur)),
  );
  const blurLabelMap = buildBlurLabelMap(blurValues);
  const blurSlider = buildSliderSteps(
    configsForAspect,
    "frameBlendBlur",
    blurLabelMap,
  );

  return {
    configs,
    configsForAspect,
    aspectRatios,
    currentConfig,
    needsConfChange,
    loading,
    error,
    refetch,
    saving,
    updateConfig,
    resolutionSlider,
    framerateSlider,
    blurSlider,
    findConfigForSelection,
    isStepAvailable,
    TIER_DISPLAY,
    resolutionLabel,
    blurLabelMap,
    DEFAULT_TIER_CONFIGS,
  };
};
